import {useEffect} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Link, useSearchParams} from 'react-router';
import {driveStatusQueryOptions, useDisconnectDrive} from '../api/admin';

export function Admin() {
  const [params, setParams] = useSearchParams();
  const justConnected = params.get('connected') === '1';
  const oauthError = params.get('error');
  const status = useQuery(driveStatusQueryOptions);
  const disconnect = useDisconnectDrive();

  useEffect(() => {
    if (justConnected || oauthError) {
      const t = window.setTimeout(() => {
        const next = new URLSearchParams(params);
        next.delete('connected');
        next.delete('error');
        setParams(next, {replace: true});
      }, 4000);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [justConnected, oauthError, params, setParams]);

  return (
    <div className="min-h-screen p-4 sm:p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <header className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Admin · Google Drive</h1>
          <Link to="/app" className="text-sm text-slate-600 hover:text-slate-900">
            ← К дашборду
          </Link>
        </header>

        {justConnected && (
          <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
            ✓ Drive подключён успешно.
          </div>
        )}
        {oauthError === 'missing_drive_scope' && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-1">
            <p className="font-medium">Не выдан доступ к Drive.</p>
            <p>
              На экране согласия Google нужно поставить галочку «See, edit, create, and
              delete all of your Google Drive files» («Просматривать, изменять, создавать и
              удалять все ваши файлы на Google Диске»). Без неё сайт не сможет загружать
              файлы и выдавать доступ к папкам анкет.
            </p>
            <p>Нажми «Connect Google Drive» ещё раз и отметь все чекбоксы.</p>
          </div>
        )}
        {oauthError && oauthError !== 'missing_drive_scope' && (
          <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">
            Ошибка OAuth: {oauthError}
          </div>
        )}

        <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
          {status.isPending && <p className="text-slate-500 text-sm">Загружаем статус…</p>}

          {status.isError && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">
              Не удалось загрузить статус: {(status.error as Error).message}.
              Проверь, что твой Telegram-id в <code>ADMIN_TELEGRAM_IDS</code> на сервере.
            </div>
          )}

          {status.data && !status.data.connected && (
            <>
              <div>
                <h2 className="font-medium text-slate-900">Drive не подключён</h2>
                <p className="text-sm text-slate-600 mt-1">
                  Нажми кнопку, войди своим Google-аккаунтом и разреши приложению
                  записывать файлы. Refresh token сохранится в Mongo, корневая папка{' '}
                  <code className="text-xs">UploaderRoot</code> создастся автоматически.
                </p>
              </div>
              <a
                href="/api/admin/google/start"
                className="inline-block rounded bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium px-4 py-2"
              >
                Connect Google Drive
              </a>
            </>
          )}

          {status.data?.connected && (
            <>
              <div>
                <h2 className="font-medium text-slate-900">Drive подключён</h2>
                <dl className="mt-3 space-y-1 text-sm">
                  <Row label="Аккаунт">{status.data.connectedAs ?? '—'}</Row>
                  <Row label="Подключён">
                    {status.data.connectedAt
                      ? new Date(status.data.connectedAt).toLocaleString()
                      : '—'}
                  </Row>
                  <Row label="Папка">
                    {status.data.rootFolderName ?? 'UploaderRoot'}{' '}
                    {status.data.rootFolderId && (
                      <a
                        href={`https://drive.google.com/drive/folders/${status.data.rootFolderId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-600 hover:underline ml-1"
                      >
                        ↗
                      </a>
                    )}
                  </Row>
                </dl>
              </div>

              <div className="flex gap-2 pt-2 border-t border-slate-100">
                <a
                  href="/api/admin/google/start"
                  className="rounded border border-slate-300 hover:border-slate-400 text-sm px-3 py-1.5"
                >
                  Переподключить
                </a>
                <button
                  type="button"
                  onClick={() => disconnect.mutate()}
                  disabled={disconnect.isPending}
                  className="rounded border border-red-300 text-red-700 hover:bg-red-50 text-sm px-3 py-1.5 disabled:opacity-50"
                >
                  {disconnect.isPending ? 'Отключаем…' : 'Отключить'}
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function Row({label, children}: {label: string; children: React.ReactNode}) {
  return (
    <div className="flex gap-3">
      <dt className="text-slate-500 w-28 shrink-0">{label}</dt>
      <dd className="text-slate-900 min-w-0 truncate">{children}</dd>
    </div>
  );
}
